// Mirrors the parts of webapp/src/utils.ts the clone depends on.
// GUID layout is upstream's: <idType char><base32 of 16 random bytes>, so mock
// data is distinguishable by prefix exactly like a real server payload.

import {Constants} from './constants'

export enum IDType {
    None = '7',
    Workspace = 'w',
    Board = 'b',
    Card = 'c',
    View = 'v',
    Session = 's',
    User = 'u',
    Token = 'k',
    BlockID = 'a',
}

// utils.ts: base32Alphabet
const base32Alphabet = 'ybndrfg8ejkmcpqxot1uwisza345h769'

export class Utils {
    static isTouch(): boolean {
        return typeof window !== 'undefined' && 'ontouchstart' in window
    }

    static randomArray(size: number): Uint8Array {
        const out = new Uint8Array(size)
        if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
            crypto.getRandomValues(out)
        } else {
            for (let i = 0; i < size; i++) {
                out[i] = Math.floor(Math.random() * 255)
            }
        }
        return out
    }

    static base32encode(data: Uint8Array, pad: boolean): string {
        let bits = 0
        let value = 0
        let output = ''
        for (let i = 0; i < data.byteLength; i++) {
            value = (value << 8) | data[i]
            bits += 8
            while (bits >= 5) {
                output += base32Alphabet[(value >>> (bits - 5)) & 31]
                bits -= 5
            }
        }
        if (bits > 0) {
            output += base32Alphabet[(value << (5 - bits)) & 31]
        }
        if (pad) {
            while (output.length % 8 !== 0) {
                output += '='
            }
        }
        return output
    }

    static createGuid(idType: IDType): string {
        const data = Utils.randomArray(16)
        return idType + Utils.base32encode(data, false)
    }

    static blockTypeToIDType(blockType: string | undefined): IDType {
        switch (blockType) {
        case 'board':
            return IDType.Board
        case 'card':
            return IDType.Card
        case 'view':
            return IDType.View
        case 'comment':
            return IDType.BlockID
        default:
            return IDType.None
        }
    }

    static createBlockId(blockType?: string): string {
        return Utils.createGuid(Utils.blockTypeToIDType(blockType))
    }

    // The clone is always served from the origin root, so buildURL() degenerates
    // to the path itself; kept to preserve upstream call sites.
    static getBaseURL(): string {
        return ''
    }

    static getApiBase(): string {
        return import.meta.env.VITE_API_BASE ?? ''
    }

    static buildURL(path: string, absolute?: boolean): string {
        const baseURL = Utils.getApiBase()
        let finalPath = path.indexOf('/') === 0 ? baseURL + path : baseURL + '/' + path
        if (absolute) {
            finalPath = window.location.origin + (finalPath.indexOf('/') === 0 ? finalPath : '/' + finalPath)
        }
        return finalPath
    }

    static sleep(milliseconds: number): Promise<void> {
        return new Promise((resolve) => window.setTimeout(resolve, milliseconds))
    }

    static safeLocalStorage(): Storage | undefined {
        try {
            return window.localStorage
        } catch {
            return undefined
        }
    }

    static getItem(id: string): string | null {
        return Utils.safeLocalStorage()?.getItem(id) ?? null
    }

    static setItem(id: string, value: string): void {
        Utils.safeLocalStorage()?.setItem(id, value)
    }

    static removeItem(id: string): void {
        Utils.safeLocalStorage()?.removeItem(id)
    }

    static deepClone<T>(value: T): T {
        if (value === undefined || value === null) {
            return value
        }
        return JSON.parse(JSON.stringify(value)) as T
    }

    static escapeRegExp(str: string): string {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }

    static truncateString(text: string, maxLength: number): string {
        if (text.length <= maxLength) {
            return text
        }
        return text.slice(0, maxLength) + '...'
    }

    static arrayMove<T>(array: T[], startIndex: number, endIndex: number, count = 1): T[] {
        const result = array.slice()
        const removed = result.splice(startIndex, count)
        result.splice(endIndex, 0, ...removed)
        return result
    }

    static hasOwnProperty<T, K extends keyof T>(obj: T, property: K): boolean {
        return Object.prototype.hasOwnProperty.call(obj, property)
    }

    static splitWorkspaceAndTeamId(pathname: string): {workspaceId: string, teamId: string} {
        const match = /^\/workspace\/([^/]+)\/?$/.exec(pathname)
        if (match) {
            return {workspaceId: match[1], teamId: match[1]}
        }
        const team = /^\/team\/([^/]+)\/?$/.exec(pathname)
        if (team) {
            return {workspaceId: team[1], teamId: team[1]}
        }
        return {workspaceId: Constants.globalTeamId, teamId: Constants.globalTeamId}
    }

    // ---- dates -----------------------------------------------------------------
    // Upstream runs these through react-intl; we call Intl directly.

    static getLocale(): string {
        // The UI ships Chinese-only: following navigator.language would leak
        // English month/day names into an otherwise translated page.
        return Constants.defaultLocale
    }

    private static yearOption(date: Date): 'numeric' | undefined {
        return date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric'
    }

    static displayDate(date: Date): string {
        return new Intl.DateTimeFormat(Utils.getLocale(), {
            year: Utils.yearOption(date),
            month: 'long',
            day: '2-digit',
        }).format(date)
    }

    static displayDateTime(date: Date): string {
        return new Intl.DateTimeFormat(Utils.getLocale(), {
            year: Utils.yearOption(date),
            month: 'long',
            day: '2-digit',
            hour: 'numeric',
            minute: 'numeric',
        }).format(date)
    }

    static inputDate(date: Date): string {
        return new Intl.DateTimeFormat('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: 'UTC',
        }).format(date)
    }

    static relativeDisplayDateTime(date: Date): string {
        const seconds = Math.round((date.getTime() - Date.now()) / 1000)
        const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
            ['year', 31536000],
            ['month', 2592000],
            ['day', 86400],
            ['hour', 3600],
            ['minute', 60],
            ['second', 1],
        ]
        const rtf = new Intl.RelativeTimeFormat(Utils.getLocale(), {numeric: 'auto'})
        for (const [unit, secs] of units) {
            if (Math.abs(seconds) >= secs || unit === 'second') {
                return rtf.format(Math.round(seconds / secs), unit)
            }
        }
        return ''
    }

    static isArchivedBlock(deleteAt: number): boolean {
        return deleteAt > 0 && deleteAt <= Date.now()
    }

    /** "2024-06-03T00:00:00.000Z" style stamps used by the mock server */
    static now(): number {
        return Date.now()
    }

    /** Milliseconds until end of `ms` from now — used by the mock latency knob. */
    static clamp(n: number, min: number, max: number): number {
        return Math.min(Math.max(n, min), max)
    }
}
